import React, { useEffect, useMemo, useState } from "react";
import { Accordion } from "react-bootstrap";
import { Box, Tooltip, CircularProgress, Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    MaterialReactTable,
    useMaterialReactTable,
    MRT_ToggleGlobalFilterButton,
    MRT_ToggleFiltersButton,
    MRT_ToggleDensePaddingButton,
    MRT_ToggleFullScreenButton,
    type MRT_ColumnDef,
} from "material-react-table";
import { fetchPnlSummaryView3, PnlRow, updateDailyChangeCloud2 } from "./Pnlsummaryview3.actions";
import { toast } from "react-toastify";

const formatNumber = (value: number) =>
    value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const PnlSummaryView3 = () => {
    const [rows, setRows] = useState<PnlRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchPnlSummaryView3();
            setRows(data);
        } catch (err) {
            console.error(err);
            setError("Failed to load P&L summary data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await fetchPnlSummaryView3();
                if (isMounted) setRows(data);
            } catch (err) {
                console.error(err);
                if (isMounted) setError("Failed to load P&L summary data");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchData();
        return () => {
            isMounted = false;
        };
    }, []);

    const handleUpdateDailyChange = async () => {
        try {
            setUpdating(true);
            setError(null);
            await updateDailyChangeCloud2();
            await loadData();
            toast.success("Daily change updated successfully");
        } catch (err) {
            console.error(err);
            setError("Failed to update daily change");
            toast.error("Failed to update daily change");
        } finally {
            setUpdating(false);
        }
    };

    const columns = useMemo<MRT_ColumnDef<PnlRow>[]>(
        () => [
            {
                accessorKey: "book",
                header: "Book",
                size: 130,
                // filterFn: "equals",
            },
            {
                accessorKey: "strategy",
                header: "Strategy",
                size: 130,
            },
            {
                accessorKey: "cost",
                header: "Cost",
                size: 130,
                Cell: ({ cell }) => {
                    const value = cell.getValue<number>();
                    return (
                        <span
                            style={{
                                color: value < 0 ? "#d32f2f" : "inherit",
                                fontWeight: 600,
                            }}
                        >
                            {formatNumber(value)}
                        </span>
                    );
                },
            },
            {
                accessorKey: "optimisation_margin",
                header: "Optimisation PnL",
                size: 170,
                Cell: ({ cell }) => {
                    const value = cell.getValue<number>();
                    return (
                        <span
                            style={{
                                color: value < 0 ? "#d32f2f" : "inherit",
                                fontWeight: 600,
                            }}
                        >
                            {formatNumber(value)}
                        </span>
                    );
                },
            },
            {
                accessorKey: "daily_change",
                header: "Gross Daily Change",
                size: 200,
                Cell: ({ cell }) => {
                    const value = cell.getValue<number>();
                    return (
                        <span
                            style={{
                                color: value < 0 ? "#d32f2f" : "inherit",
                                fontWeight: 600,
                            }}
                        >
                            {formatNumber(value)}
                        </span>
                    );
                },
            },
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data: rows,
        enableColumnFilters: true,
        enableSorting: true,
        enablePagination: false,
        enablePinning: true,
        state: {
            isLoading: loading,
            showColumnFilters: true,
        },
        initialState: {
            showColumnFilters: true,
            density: "compact",
            columnPinning: { left: ["book", "strategy"] },
        },
        columnFilterDisplayMode: "subheader",
        layoutMode: "grid-no-grow",
        muiTableContainerProps: {
            sx: {
                maxHeight: "70vh",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                backgroundColor: "#ffffff",
            },
        },
        renderTopToolbarCustomActions: ({ table }) => {
            const filteredRows = table.getFilteredRowModel().rows;
            const totalDailyChange = filteredRows.reduce(
                (sum, row) => sum + (Number(row.original.daily_change) || 0),
                0
            );

            const bookColWidth = table.getColumn("book")?.getSize() ?? 130;
            const strategyColWidth = table.getColumn("strategy")?.getSize() ?? 130;
            const costColWidth = table.getColumn("cost")?.getSize() ?? 130;
            const optimisationColWidth = table.getColumn("optimisation_margin")?.getSize() ?? 150;

            return (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        width: "100%",
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            pl: 1,
                        }}
                    >
                        Total :
                    </Box>

                    <Box
                        sx={{
                            width: { xs: 0, sm: `${bookColWidth + strategyColWidth + costColWidth + optimisationColWidth}px` },
                            flexShrink: 0,
                            display: { xs: "none", sm: "block" },
                        }}
                    />

                    <Box
                        component="span"
                        sx={{
                            backgroundColor: totalDailyChange < 0 ? "#f8d7da" : "#a5d6a7",
                            color: totalDailyChange < 0 ? "#842029" : "inherit",
                            padding: "4px 14px",
                            borderRadius: "4px",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {formatNumber(totalDailyChange)}
                    </Box>
                </Box>
            );
        },
        renderToolbarInternalActions: ({ table }) => (
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Update Daily Change">
                    <span>
                        <Button
                            onClick={handleUpdateDailyChange}
                            disabled={updating}
                            size="small"
                            variant="outlined"
                            startIcon={
                                updating ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : (
                                    <RefreshIcon fontSize="small" />
                                )
                            }
                            sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                borderRadius: "6px",
                                borderColor: "#c9d4dd",
                                color: "#fff !important",
                                marginRight: 5,
                                backgroundColor: "#585bcc",
                                padding: "3px 10px",
                                minWidth: "auto",
                                "&:hover": {
                                    backgroundColor: "#585bcc",
                                    borderColor: "#90a4ae",
                                },
                            }}
                        >
                            {updating ? "Refreshing..." : "Refresh"}
                        </Button>
                    </span>
                </Tooltip>
                <MRT_ToggleGlobalFilterButton table={table} />
                <MRT_ToggleFiltersButton table={table} />
                <MRT_ToggleDensePaddingButton table={table} />
                <MRT_ToggleFullScreenButton table={table} />
            </Box>
        ),
        muiTableHeadRowProps: {
            sx: {
                position: "sticky",
                top: 0,
                background: "white",
                zIndex: 5,
                "& .MuiTableCell-root": {
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "#2c3e50",
                    borderRight: "1px solid #dcdcdc",
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                fontSize: "0.7rem",
                padding: "4px 8px",
                borderBottom: "1px solid #f1f1f1",
                "&:hover": {
                    backgroundColor: "#fafafa",
                },
            },
        },
        muiBottomToolbarProps: {
            sx: {
                backgroundColor: "#fff",
                padding: "8px",
            },
            className: "bottom-toolbar-wrapper__new",
        },
        muiTopToolbarProps: {
            sx: {
                backgroundColor: "#fff",
                padding: "8px",
                "& .MuiBox-root.css-wsew38": {
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                },
            },
            className: "top-toolbar-wrapper__new",
        },
        muiPaginationProps: {
            shape: "rounded",
            size: "small",
            sx: {
                fontSize: "0.7rem",
                "& .MuiButtonBase-root": {
                    fontSize: "0.7rem",
                    minWidth: "28px",
                    height: "28px",
                },
            },
        },
        muiToolbarAlertBannerProps: {
            sx: {
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
                fontSize: "0.75rem",
                borderRadius: 1,
            },
        },
    });

    return (
        <div className="page-body">
            <div className="table-ad__wrapper typeCapitalize">
                <Accordion
                    defaultActiveKey="0"
                    className="table-ad__addInputs"
                    style={{ width: "100%" }}
                >
                    <Accordion.Item eventKey="0">
                        <Accordion.Header
                            className="accordion_button"
                            style={{
                                fontSize: "0.8rem",
                                backgroundColor: "#d1edf1",
                                padding: "6px 12px",
                                borderRadius: "4px",
                                fontWeight: 600,
                            }}
                        >
                            P&L Summary View-3
                        </Accordion.Header>

                        <Accordion.Body>
                            {error && (
                                <div
                                    style={{
                                        color: "#d32f2f",
                                        fontSize: "0.8rem",
                                        marginBottom: 8,
                                    }}
                                >
                                    {error}
                                </div>
                            )}

                            <div className="table-ad__wrapperCard">
                                <MaterialReactTable table={table} />
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </div>
        </div>
    );
};

export default PnlSummaryView3;